/* eslint-disable react/no-unstable-nested-components */
import { ReturnSourceContext } from "@api/ReturnProductManagementApi";
import PageTitle from "@component/PageTitle";
import ReturnSourceType from "@constant/ReturnSourceType";
import useIntRouteParam from "@hooks/useIntRouteParam";
import { fetchSourceOrderPrefill, SourceOrderPrefill } from "@redux/sharing/ReturnProductManagementSlice";
import { useAppDispatch } from "src/store";

import React, { useEffect, useState } from "react";
import { Steps } from "antd";
import { useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";

import ReturnProductAppCreateStep1, { Step1FormValues } from "./ReturnProductAppCreateStep1";
import ReturnProductAppCreateStep2, { Step2ProductRow } from "./ReturnProductAppCreateStep2";
import ReturnProductAppCreateStep3 from "./ReturnProductAppCreateStep3";

const Wrapper = styled.div`
  padding: 20px 25px;
`;
const Content = styled.div`
  margin-top: 20px;
  padding-bottom: 100px;
`;

export default function ReturnProductAppCreate() {
  const dispatch = useAppDispatch();
  const { sourceType: sourceTypeParam } = useParams();
  const orderId = useIntRouteParam("orderId", true);
  const [searchParams] = useSearchParams();
  const subOrderIdParam = searchParams.get("subOrderId");
  const subOrderId = subOrderIdParam ? Number(subOrderIdParam) : undefined;

  const sourceType = Object.values(ReturnSourceType).find((value) => value === sourceTypeParam);

  const [currentStep, setCurrentStep] = useState(0);
  const [isFetchingPrefill, setIsFetchingPrefill] = useState(false);
  const [isPrefillError, setIsPrefillError] = useState(false);
  const [prefill, setPrefill] = useState<SourceOrderPrefill>();
  const [step1Values, setStep1Values] = useState<Step1FormValues>();
  const [productRows, setProductRows] = useState<Step2ProductRow[]>([]);

  const fetchPrefill = () => {
    if (!sourceType) return;
    setIsFetchingPrefill(true);
    dispatch(fetchSourceOrderPrefill({ sourceType, orderId, subOrderId }))
      .unwrap()
      .then(setPrefill)
      .catch(() => setIsPrefillError(true))
      .finally(() => setIsFetchingPrefill(false));
  };

  useEffect(() => {
    fetchPrefill();
    // 僅在進入此頁時撈取一次來源訂單預填資料
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetryPrefill = () => {
    setIsPrefillError(false);
    fetchPrefill();
  };

  if (!sourceType) {
    return <PageTitle title="退貨管理" />;
  }

  const sourceContext: ReturnSourceContext = { sourceType, orderId, subOrderId };

  const handleStep1Next = (values: Step1FormValues) => {
    setStep1Values(values);
    setCurrentStep(1);
  };

  const StepComponent = () => {
    switch (currentStep) {
      case 0:
        return (
          <ReturnProductAppCreateStep1
            prefill={prefill}
            isFetching={isFetchingPrefill}
            isError={isPrefillError}
            onRetry={handleRetryPrefill}
            initialValues={step1Values}
            onNextStep={handleStep1Next}
          />
        );
      case 1:
        return (
          <ReturnProductAppCreateStep2
            sourceContext={sourceContext}
            productRows={productRows}
            onChangeProductRows={setProductRows}
            onBackStep={() => setCurrentStep(0)}
            onNextStep={() => setCurrentStep(2)}
          />
        );
      case 2:
        return step1Values && prefill ? (
          <ReturnProductAppCreateStep3
            prefill={prefill}
            step1Values={step1Values}
            productRows={productRows}
            onBackStep={() => setCurrentStep(1)}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <Wrapper>
      <PageTitle title="退貨管理 - 新增退貨單" />
      <Steps current={currentStep}>
        <Steps.Step title="建立退貨單基本資料" />
        <Steps.Step title="選擇退貨商品" />
        <Steps.Step title="退貨單預覽" />
      </Steps>
      <Content>{StepComponent()}</Content>
    </Wrapper>
  );
}
